<template>
  <b-modal
    v-model="showModal"
    id="library-modal"
    size="lg"
    hide-footer
    centered
    title="Video Library"
    @show="loadVideos"
    @hidden="closeModal"
  >
    <div class="library-modal-content">
      <p class="modal-description">
        Select videos from your library to add to your video generation.
      </p>

      <!-- Loading State - Skeleton -->
      <div
        v-if="loading"
        class="video-options-grid skeleton-grid"
      >
        <div
          v-for="n in 20"
          :key="`skeleton-${n}`"
          class="video-option skeleton-item"
        >
          <div class="skeleton-video" />
          <div class="skeleton-text skeleton-title" />
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="error-container"
      >
        <div class="error-icon">
          <feather-icon
            icon="AlertCircleIcon"
            size="48"
          />
        </div>
        <p class="error-text">
          {{ error }}
        </p>
        <b-button
          variant="outline-primary"
          @click="loadVideos"
        >
          Try Again
        </b-button>
      </div>

      <!-- Video Grid -->
      <div
        v-else
        class="video-options-grid"
        @scroll="handleScroll"
      >
        <VideoCard
          v-for="video in videos"
          :key="video.id"
          :video-id="video.id"
          :video-url="getVideoUrl(video)"
          :thumbnail-url="getPoster(video)"
          :is-selected="isVideoSelected(video.id)"
          :is-playing="getCardState(video.id, 'playing')"
          selection-type="checkbox"
          @select="handleVideoSelect"
          @update:is-playing="handleVideoPlayingUpdate"
        />
      </div>

      <!-- Load More Indicator -->
      <div
        v-if="!loading && !error && hasMore"
        class="load-more-container"
      >
        <div
          v-if="loadingMore"
          class="loading-more"
        >
          <div class="spinner-border spinner-border-sm text-primary" />
          <span class="loading-text">Loading more videos...</span>
        </div>
        <div
          v-else
          class="load-more-info"
        >
          Showing {{ videos.length }} of {{ total }} videos
        </div>
      </div>

      <!-- No More Data -->
      <div
        v-if="!loading && !error && !hasMore && videos.length > 0"
        class="no-more-data"
      >
        <div class="no-more-text">
          All {{ total }} videos loaded
        </div>
      </div>

    </div>

    <div class="modal-actions">
      <b-button
        variant="outline-secondary"
        size="sm"
        @click="cancelSelection"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        size="sm"
        class="primary-gradient-btn"
        @click="confirmSelection"
      >
        Update
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton } from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import VideoCard from './VideoCard.vue'

export default {
  name: 'LibraryModal',
  components: {
    BModal,
    BButton,
    VideoCard,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentSelectedVideos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showModal: this.show,
      videos: [],
      loading: false,
      loadingMore: false,
      error: null,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,
      selectedVideoIds: [],
      cardStates: {},
    }
  },
  computed: {
    ...mapGetters('videoGeneration', [
      'videoListVideos',
      'videoListLoading',
      'videoListPagination',
    ]),
    selectedVideos() {
      return this.videos.filter(video => this.selectedVideoIds.includes(video.id))
    },
  },
  watch: {
    show: {
      handler(newVal) {
        this.showModal = newVal
        if (newVal) {
          this.initializeSelection()
        }
      },
      immediate: true,
    },
    currentSelectedVideos: {
      handler(newVal = []) {
        this.selectedVideoIds = newVal.map(video => video?.id).filter(Boolean)
      },
      immediate: true,
    },
    videos() {
      this.$nextTick(() => {
        this.ensureCardStates()
      })
    },
  },
  methods: {
    ...mapActions('videoGeneration', [
      'fetchVideoList',
    ]),

    initializeSelection() {
      this.selectedVideoIds = this.currentSelectedVideos.map(video => video?.id).filter(Boolean)
    },

    async loadVideos(page = 1, append = false) {
      const pageNumber = Number(page) || 1

      if (append) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.videos = []
        this.currentPage = 1
        this.hasMore = true
      }
      this.error = null

      try {
        await this.fetchVideoList({
          page: pageNumber,
          limit: this.pageSize,
          types: ['IMAGE_TO_BYTEPLUS_VIDEO', 'MANUAL_UPLOAD_SOURCE_VIDEO', 'IMAGE_ANIMATION'],
        })

        // Get videos from store
        const newVideos = this.videoListVideos || []

        if (append) {
          this.videos = [...this.videos, ...newVideos]
        } else {
          this.videos = newVideos
        }

        // Handle pagination
        const pagination = this.videoListPagination || {}
        this.currentPage = Number(pagination.page || pagination.currentPage || 1)
        this.total = Number(pagination.total || pagination.totalCount || newVideos.length)
        this.hasMore = this.videos.length < this.total
      } catch (error) {
        this.error = 'Failed to load videos. Please try again.'
        this.$toast.error('Failed to load videos')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    async loadMore() {
      if (this.hasMore && !this.loadingMore) {
        const nextPage = Number(this.currentPage) + 1
        await this.loadVideos(nextPage, true)
      }
    },

    handleScroll(event) {
      const { target } = event
      const { scrollTop, scrollHeight, clientHeight } = target

      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100

      if (isNearBottom && this.hasMore && !this.loadingMore) {
        this.loadMore()
      }
    },

    ensureCardStates() {
      this.videos.forEach(video => {
        this.ensureCardState(video)
      })
    },

    ensureCardState(video) {
      if (!video?.id) return
      const existing = this.cardStates[video.id] || {}
      this.$set(this.cardStates, video.id, {
        playing: existing.playing ?? false,
      })
      this.$nextTick(() => this.syncVideoElement(video.id))
    },

    updateCardState(id, updates) {
      if (!id) return
      const prev = this.cardStates[id] || {}
      this.$set(this.cardStates, id, { ...prev, ...updates })
    },

    getCardState(id, key) {
      const state = this.cardStates[id]
      if (!state) {
        return key === 'playing' ? false : undefined
      }
      if (key === 'playing') return state.playing === true
      return state[key]
    },

    handleVideoSelect(videoId) {
      const video = this.videos.find(v => v.id === videoId)
      if (!video) return
      this.toggleVideoSelection(video)
    },

    handleVideoPlayingUpdate(videoId, isPlaying) {
      this.updateCardState(videoId, { playing: isPlaying })
    },

    isVideoSelected(id) {
      return this.selectedVideoIds.includes(id)
    },

    toggleVideoSelection(video) {
      if (!video?.id) return
      const videoUrl = this.getVideoUrl(video)
      if (!videoUrl) {
        this.$toast.warning('This video is not ready for selection yet.')
        return
      }

      const currentIndex = this.selectedVideoIds.indexOf(video.id)
      if (currentIndex > -1) {
        // Remove from selection
        this.selectedVideoIds.splice(currentIndex, 1)
      } else {
        // Add to selection
        this.selectedVideoIds.push(video.id)
      }

      // Don't emit immediately - only emit when Update is clicked
      // This prevents the selection from being applied before confirmation
    },

    getVideoUrl(video) {
      return video?.videoPolling?.videoUrl
        || video?.videoGeneration?.result?.previewUrl
        || video?.previewUrl
        || ''
    },

    getPoster(video) {
      return video?.imageUrl || video?.videoGeneration?.result?.thumbnailUrl || ''
    },

    getVideoDomId(video) {
      if (!video?.id) return null
      return `library-video-${String(video.id)}`
    },

    getVideoElement(id) {
      if (!id) return null
      const domId = `library-video-${String(id)}`
      try {
        return this.$el && this.$el.querySelector ? this.$el.querySelector(`#${domId}`) : null
      } catch {
        return null
      }
    },

    syncVideoElement(id) {
      const el = this.getVideoElement(id)
      if (!el) return
      el.muted = true
      el.controls = false
    },

    buildSelectionRecord(video) {
      return {
        id: video.id,
        taskId: video?.videoGeneration?.taskId
          || video?.videoGeneration?.result?.taskId
          || video?.videoGeneration?.result?.task_id
          || video?.taskId
          || video?.task_id,
        videoUrl: this.getVideoUrl(video),
        imageUrl: video.imageUrl
          || video.thumbnailUrl
          || video?.videoGeneration?.result?.thumbnailUrl
          || '',
        status: video?.videoPolling?.status
          || video?.status
          || video?.videoGeneration?.result?.status
          || '',
        prompt: video?.prompt
          || video?.videoGeneration?.result?.promptSetting?.textPrompt
          || video?.promptSetting?.textPrompt
          || '',
      }
    },

    confirmSelection() {
      // Always emit the current selection state, even if empty
      const selectedVideoRecords = this.selectedVideos.map(video => this.buildSelectionRecord(video))
      this.$emit('videos-selected', selectedVideoRecords)
      this.closeModal()
    },

    cancelSelection() {
      // Reset selection to original state and close without applying changes
      this.selectedVideoIds = [...this.currentSelectedVideos.map(video => video?.id).filter(Boolean)]
      this.$emit('close')
    },

    emitCurrentSelection() {
      // Emit current selection state to parent for real-time updates
      const currentSelectedVideos = this.selectedVideos.map(video => this.buildSelectionRecord(video))
      this.$emit('selection-changed', currentSelectedVideos)
    },

    closeModal() {
      // This is called by the modal's @hidden event and other internal closes
      // Don't emit selection changes here - let the specific actions handle it
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.library-modal-content {
  .modal-description {
    margin-bottom: 10px;
    color: #666;
    font-size: 14px;
  }

  .video-options-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    max-height: 450px;
    overflow-y: auto;
    padding: 8px 8px 0 0;
    scroll-behavior: smooth;
  }

}

.load-more-container {
  margin-top: 15px;
  padding: 0 16px;
  text-align: right;

  .loading-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    color: #666;
    font-size: 14px;

    .loading-text {
      margin: 0;
    }
  }

  .load-more-info {
    color: #666;
    font-size: 14px;
    font-style: italic;
  }
}

.no-more-data {
  margin-top: 20px;
  padding: 16px;
  text-align: center;

  .no-more-text {
    color: #28a745;
    font-size: 14px;
    font-weight: 500;
  }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  .error-icon {
    color: #dc3545;
    margin-bottom: 16px;
  }

  .error-text {
    color: #666;
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;

  .btn {
    border-radius: 20px !important;
    padding: 8px 16px;
    font-weight: 600;
  }

  .btn-outline-secondary {
    border: 1px solid #6c757d !important;
    color: #6c757d !important;
    background: transparent !important;

    &:hover,
    &:focus,
    &:active {
      background: #6c757d !important;
      color: #ffffff !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
    }
  }

  .primary-gradient-btn {
    background: linear-gradient(135deg, #8ec5fc 0%, #a855f7 100%) !important;
    border: none !important;
    color: #ffffff !important;

    &:hover,
    &:focus,
    &:active {
      background: linear-gradient(135deg, #7bb3f9 0%, #9333ea 100%) !important;
      color: #ffffff !important;
      box-shadow: 0 4px 12px rgba(142, 197, 252, 0.4);
      transform: translateY(-1px);
    }

    &:disabled {
      background: #e5e7eb !important;
      color: #9ca3af !important;
      transform: none;
      box-shadow: none;
    }
  }
}

/* Skeleton Loading Styles */
.skeleton-grid {
  .skeleton-item {
    position: relative;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 12px;
    cursor: default;
    pointer-events: none;

    .skeleton-video {
      width: 100%;
      height: 120px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 6px;
      margin-bottom: 8px;
    }

    .skeleton-text {
      height: 12px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 4px;
      margin-bottom: 4px;

      &.skeleton-title {
        width: 80%;
        margin-bottom: 0;
      }
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
